import { useState, useRef, useEffect } from "react";

import cn from "classnames";
import { Formik } from "formik";
import { TextField, Switch, Typography, FormControlLabel, InputAdornment, ClickAwayListener, useMediaQuery } from "@mui/material";
import { MuiTelInput, MuiTelInputCountry } from "mui-tel-input";
import Link from "next/link";
import Image from "next/image";

import { BaseButton } from "@/src/component/base/BaseButton";
import { APP_TENANT } from "@/src/const";
import { ProviderHooks } from "@/src/hook";
import { TrackingLogic, ContactUsLogic } from "@/src/logic";
import { FrontendLeadService, FrontendTrackingService } from "@/src/service";
import { LeadBody } from "@/src/type/contact";
import { FormHelper, UUIDHelper } from "@/src/util";

import { FormStyles } from "@/styles/util";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./ContactForm.module.scss";

type ContactUsFormLabelType = {
  firstNameLabel?: string;
  lastNameLabel?: string;
  emailLabel?: string;
  phoneNumberLabel?: string;
  whatsAppLabel?: string;
  descriptionLabel?: string;
  submissionDisclaimer?: string;
  submissionDisclaimerLinks?: {
    href: string;
    isExternal: boolean;
    target?: string;
    label: string;
  }[];
  submitButtonLabel?: string;
  descriptionPlaceholder?: string;
};

interface ContactFormProps {
  metadata?: Record<string, string>;
  type: string;
  onSuccess?: (lead: LeadBody) => void;
  labels: ContactUsFormLabelType;
  grid2x2?: boolean;
  isDark?: boolean;
  disableAnimatedLabel?: boolean;
  formId?: string;
}

function ContactForm({ metadata, type, onSuccess, labels, grid2x2, isDark, disableAnimatedLabel, formId }: ContactFormProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [crmLinkId] = useState<string>(UUIDHelper.createUUID(true));
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [menuListenerMounted, setMenuListenerMounted] = useState<boolean>(false);
  const [defaultCountry, setDefaultCountry] = useState<MuiTelInputCountry>("US");
  const phoneInputRef = useRef<HTMLElement>();
  const heightToSmall = useMediaQuery("(max-width: 760px)");

  const toast = ProviderHooks.useToast();
  const cms = ProviderHooks.useCMS();
  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "",
    phoneNumber: "",
    whatsApp: "",
    description: "",
    agreeToSMS: false,
    agreeToContact: true,
  };

  function getHref(href: string, isExternal: boolean): string {
    return isExternal ? href : cms.constructLink(href);
  }

  const handleMenuOpen = () => {
    if (!menuOpen) {
      setMenuOpen(true);
    }
  };

  const handleMenuClose = () => {
    if (menuOpen) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const locationData = await TrackingLogic.getLocationData();
        setDefaultCountry((locationData?.country ?? "US") as MuiTelInputCountry);
      } catch (error) {
        console.error("Error fetching location data:", error);
      }
    })();
  }, []);

  useEffect(() => {
    if (menuListenerMounted || !phoneInputRef?.current) {
      return;
    }

    const button = phoneInputRef?.current.querySelector("button[class*='MuiTelInput-IconButton']");
    if (button) {
      button.addEventListener("click", handleMenuOpen);
      setMenuListenerMounted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneInputRef?.current, menuListenerMounted]);

  return (
    <div className={styles.container}>
      <Formik
        initialValues={initialValues}
        validationSchema={ContactUsLogic.generalContactUsValidationSchema(cms?.webConfig?.formValidation)}
        onSubmit={async (values) => {
          if (submitted) {
            toast.addMessage({
              title: "We've received your message",
              subtitle: "Thank you for your message, a member of our team will be in touch shortly.",
              type: "success",
            });
            return;
          }

          const clone = JSON.parse(JSON.stringify(values));
          delete clone.agreeToContact;
          const extraMetadata = metadata
            ? Object.entries(metadata).map(([key, value]) => ({
                key,
                value,
                name: `${key}:${value}`,
              }))
            : [];

          setLoading(true);
          const trackingParams = TrackingLogic.getUTMParams();
          const leadBody = {
            form: {
              ...clone,
              source: APP_TENANT,
              tracking: {
                referrer: trackingParams.referrer,
                utmSource: trackingParams.utmSource,
                utmMedium: trackingParams.utmMedium,
                utmCampaign: trackingParams.utmCampaign,
                utmContent: trackingParams.utmContent,
                utmTerm: trackingParams.utmTerm,
                sessionId: await TrackingLogic.getSessionId(),
                googleCLID: trackingParams.googleCLID,
                facebookCLID: trackingParams.facebookCLID,
                persistentId: TrackingLogic.getPersistentId(),
              },
              metadata: [
                ...extraMetadata,
                {
                  key: "leadType",
                  value: type,
                  name: `leadType:${type}`,
                },
                {
                  key: "agreeToContact",
                  value: `${values.agreeToContact}`,
                  name: `agreeToContact:${values.agreeToContact}`,
                },
                {
                  key: "crmLinkId",
                  value: crmLinkId,
                  name: `crmLinkId:${crmLinkId}`,
                },
              ],
            },
            fub: {
              campaign: {
                source: trackingParams.utmSource,
                campaign: trackingParams.utmCampaign,
                content: trackingParams.utmContent,
                term: trackingParams.utmTerm,
                medium: trackingParams.utmMedium,
              },
              pageTitle: document.title,
              pageUrl: window.location.href,
              pageReferrer: trackingParams.referrer,
            },
          };

          const postContactForm = await FrontendLeadService.postLeadForm(leadBody);
          if (postContactForm.success) {
            if (typeof onSuccess === "function") {
              onSuccess(leadBody);
            }

            const leadId = postContactForm?.response?.payload?.data?.id;
            FrontendTrackingService.postTrackingLog({
              action: "click",
              actionId: leadId ? `${leadId}` : "contactUsSubmitted",
              actionIdType: "strapi.lead.id",
              actionMessage: "contactUsSubmitted",
              triggeredFrom: "ContactForm.tsx",
              pushTo: {
                meta: "ContactUs-Submit",
                googleAnalytics: true,
                googleAds: true,
                hubspot: true,
              },
            });

            toast.addMessage({
              title: "Contact Us Message Success",
              subtitle: "Your message was successfully sent.",
              type: "success",
            });

            setSubmitted(true);
          } else {
            toast.addMessage({
              title: "Contact Us Message Failed",
              subtitle: "Sorry, your message could not be sent at this time.",
              type: "error",
            });
          }

          setLoading(false);
        }}
      >
        {(formik) => (
          <form
            onSubmit={formik.handleSubmit}
            id={formId ?? "ContactForm"}
            suppressHydrationWarning={true}
          >
            <div className={styles.form}>
              <div className={grid2x2 ? styles.grid2x2 : styles.singleColumn}>
                <TextField
                  name="firstName"
                  label={labels?.firstNameLabel}
                  aria-label={labels?.firstNameLabel}
                  placeholder={labels?.firstNameLabel}
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Image
                          src="/image/icon_profile.svg"
                          width={24}
                          height={24}
                          alt="First Name Icon"
                        />
                      </InputAdornment>
                    ),
                  }}
                  helperText={FormHelper.formikErrorMessage<any>(formik, "firstName")}
                  error={FormHelper.formikCheckError<any>(formik, "firstName")}
                  inputProps={{
                    maxLength: 50,
                  }}
                  sx={{ ...FormStyles.themeStyle(isDark), ...FormStyles.handleLabel(formik.values.firstName, disableAnimatedLabel) }}
                />
                <TextField
                  name="lastName"
                  label={labels?.lastNameLabel}
                  aria-label={labels?.lastNameLabel}
                  placeholder={labels?.lastNameLabel}
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Image
                          src="/image/icon_profile.svg"
                          width={24}
                          height={24}
                          alt="Last Name Icon"
                        />
                      </InputAdornment>
                    ),
                  }}
                  helperText={FormHelper.formikErrorMessage<any>(formik, "lastName")}
                  error={FormHelper.formikCheckError<any>(formik, "lastName")}
                  inputProps={{
                    maxLength: 50,
                  }}
                  sx={{ ...FormStyles.themeStyle(isDark), ...FormStyles.handleLabel(formik.values.lastName, disableAnimatedLabel) }}
                />
              </div>
              <div className={grid2x2 ? styles.grid2x2 : styles.singleColumn}>
                <TextField
                  name="email"
                  label={labels?.emailLabel}
                  aria-label={labels?.emailLabel}
                  placeholder={labels?.emailLabel}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Image
                          src="/image/icon_email.svg"
                          width={24}
                          height={24}
                          alt="Email Icon"
                        />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  helperText={FormHelper.formikErrorMessage<any>(formik, "email")}
                  error={FormHelper.formikCheckError<any>(formik, "email")}
                  inputProps={{
                    maxLength: 50,
                  }}
                  sx={{ ...FormStyles.themeStyle(isDark), ...FormStyles.handleLabel(formik.values.email, disableAnimatedLabel) }}
                />
                <ClickAwayListener onClickAway={handleMenuClose}>
                  <MuiTelInput
                    name="phoneNumber"
                    className={styles.phoneNumberInput}
                    value={formik.values.phoneNumber}
                    label={labels?.phoneNumberLabel}
                    aria-label={labels?.phoneNumberLabel}
                    placeholder={labels?.phoneNumberLabel}
                    forceCallingCode={true}
                    defaultCountry={defaultCountry ?? "US"}
                    onChange={(value /*, countryInfo*/) => {
                      formik.setFieldValue("phoneNumber", value);
                    }}
                    unknownFlagElement={
                      <Image
                        src="/image/icon_phone_dark.svg"
                        width={24}
                        height={24}
                        alt="World Icon"
                      />
                    }
                    sx={{
                      ...FormStyles.themeStyle(isDark),
                      ...FormStyles.handleLabel(formik.values.phoneNumber, disableAnimatedLabel),
                      "& .MuiInputAdornment-positionStart": {
                        "& .MuiTypography-body1": {
                          paddingLeft: "4px",
                          fontSize: "15px",
                        },
                      },
                    }}
                    InputProps={{
                      ref: phoneInputRef,
                    }}
                    MenuProps={{
                      open: menuOpen,
                      variant: "menu",
                      anchorEl: phoneInputRef.current,
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      componentsProps: {
                        root: {
                          style: {
                            top: "2px",
                          },
                        },
                      },
                      slotProps: {
                        paper: {
                          style: {
                            pointerEvents: "auto",
                            height: "auto",
                            maxHeight: heightToSmall ? "200px" : "200px",
                            boxShadow: "0px 5px 5px -3px rgba(0,0,0,0.3), 5px 0px 5px -3px rgba(0,0,0,0.3), -5px 0px 5px -3px rgba(0,0,0,0.3)",
                            maxWidth: phoneInputRef?.current?.getBoundingClientRect().width,
                          },
                        },
                      },
                    }}
                    helperText={FormHelper.formikErrorMessage<any>(formik, "phoneNumber")}
                    error={FormHelper.formikCheckError<any>(formik, "phoneNumber")}
                  />
                </ClickAwayListener>
              </div>
              <TextField
                name="description"
                multiline={true}
                rows={4}
                fullWidth
                label={labels?.descriptionLabel}
                aria-label={labels?.descriptionLabel}
                placeholder={labels?.descriptionPlaceholder}
                value={formik.values.description}
                onChange={formik.handleChange}
                helperText={FormHelper.formikErrorMessage<any>(formik, "description")}
                error={FormHelper.formikCheckError<any>(formik, "description")}
                inputProps={{
                  maxLength: 6000,
                }}
                sx={{
                  ...FormStyles.themeStyle(isDark),
                  ...(disableAnimatedLabel ? FormStyles.handleLabel(formik.values.description, disableAnimatedLabel) : undefined),
                }}
              />
              <div className={styles.checkboxContainer}>
                <FormControlLabel
                  control={
                    <Switch
                      name="agreeToContact"
                      checked={formik.values.agreeToContact}
                      onChange={() => {
                        formik.setFieldValue("agreeToContact", !formik.values.agreeToContact);
                        FrontendTrackingService.postTrackingLog({
                          action: "click",
                          actionId: "agreeToContact",
                          actionIdType: "field",
                          actionMessage: `agreeToContact: ${!formik.values.agreeToContact}`,
                          triggeredFrom: "ContactForm.tsx",
                        });
                      }}
                      sx={FormStyles.checkboxStyle(isDark)}
                    />
                  }
                  label={
                    <div className={styles.disclaimerCheckboxLabel}>
                      <Typography
                        className={cn(styles.checkboxLabel)}
                        color={isDark ? theme.primaryMain : theme.secondaryMain}
                      >
                        {labels?.submissionDisclaimer}
                        {(labels?.submissionDisclaimerLinks ?? []).map((link: any, i: any) => (
                          <span key={i}>
                            {i > 0 ? " & " : " "}
                            <Link
                              key={i}
                              href={getHref(link.href, link.isExternal)}
                              target={link.target ?? "_blank"}
                            >
                              {link.label}
                            </Link>
                          </span>
                        ))}
                      </Typography>
                    </div>
                  }
                />
              </div>
              <div className={styles.hidden}>
                <input
                  type="text"
                  name="utmSource"
                  id="utmSource"
                  value={TrackingLogic.getUTMParams().utmSource ?? ""}
                />
                <input
                  type="text"
                  name="utmMedium"
                  id="utmMedium"
                  value={TrackingLogic.getUTMParams().utmMedium ?? ""}
                />
                <input
                  type="text"
                  name="utmTerm"
                  id="utmTerm"
                  value={TrackingLogic.getUTMParams().utmTerm ?? ""}
                />
                <input
                  type="text"
                  name="utmCampaign"
                  id="utmCampaign"
                  value={TrackingLogic.getUTMParams().utmCampaign ?? ""}
                />
                <input
                  type="text"
                  name="utmContent"
                  id="utmContent"
                  value={TrackingLogic.getUTMParams().utmContent ?? ""}
                />
                <input
                  type="text"
                  name="referrer"
                  id="referrer"
                  value={TrackingLogic.getUTMParams().referrer ?? ""}
                />
                <input
                  type="text"
                  name="facebookCLID"
                  id="facebookCLID"
                  value={TrackingLogic.getUTMParams().facebookCLID ?? ""}
                />
                <input
                  type="text"
                  name="googleCLID"
                  id="googleCLID"
                  value={TrackingLogic.getUTMParams().googleCLID ?? ""}
                />
                <input
                  type="text"
                  name="crmLinkId"
                  id="crmLinkId"
                  value={crmLinkId}
                />
              </div>
              <div className={styles.formBtn}>
                <BaseButton
                  id={"contactForm.submitButton"}
                  aria-label={labels?.submitButtonLabel ?? "Submit button"}
                  type="submit"
                  variant="outlined"
                  loading={loading}
                  color={isDark ? "primary" : "secondary"}
                  size="large"
                  sx={{
                    "&:disabled": {
                      borderColor: `${isDark ? theme.primaryMain : theme.secondaryMain} !important`,
                      color: `${isDark ? theme.primaryMain : theme.secondaryMain} !important`,
                    },
                  }}
                  onClick={() => {
                    FrontendTrackingService.postTrackingLog({
                      action: "click",
                      actionId: formId ?? "contactForm",
                      actionIdType: "id",
                      actionMessage: labels?.submitButtonLabel ?? "Submit button",
                      triggeredFrom: "ContactForm.tsx:(onClick submit button)",
                      pushTo: {
                        googleAnalytics: "contactus_submit",
                        googleAds: true,
                        hubspot: true,
                        meta: "contactus_submit",
                      },
                    });
                  }}
                >
                  {labels?.submitButtonLabel}
                </BaseButton>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

export default ContactForm;
